import { type RichTextElement, type Leaf, type Content } from '@/features/cms/components/CMS/types'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { type FAQPage } from 'schema-dts'

// The answer field is a rich text content type. This util helps convert it to plain text.
const richTextToPlainText = (richTextElement: RichTextElement | Leaf): string => {
  if ('text' in richTextElement) {
    return richTextElement.text ?? ''
  } else if ('children' in richTextElement) {
    const childrenText = richTextElement.children
      .map((child) => richTextToPlainText(child))
      .join('')
      .trim()

    // Add space after block elements
    if (['p', 'li', 'ol', 'ul', 'h1', 'h2', 'h3'].includes(richTextElement.type) && childrenText) {
      return childrenText + ' '
    }

    return childrenText
  }
  return ''
}

export const getFAQSchema = ({
  canonicalUrl,
  bodyContent,
}: {
  canonicalUrl: string
  bodyContent: Content[]
}): FAQPage | undefined => {
  const faqContent = bodyContent.find(
    ({ analytic_slug }) =>
      analytic_slug?.startsWith('split_section_seo_faqs_') ||
      analytic_slug?.startsWith('split_section_faq_'),
  )

  if (
    faqContent?.data &&
    'sections' in faqContent.data &&
    'accordion_sections' in faqContent.data.sections[1].data
  ) {
    const faqEntries =
      // FAQ split sections have structure: [ImageBlock, AccordionBlock]
      // The accordion with FAQ entries is always at index 1
      faqContent.data.sections[1].data.accordion_sections.map(({ data: entry }) => {
        // Loop over each element in the "answer" content to convert rich text to plain text and remove extra spaces
        const answer = entry.content.elements
          .reduce((answerString, element) => answerString + richTextToPlainText(element), '')
          .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
          .trim()

        return {
          question: entry.heading,
          answer,
        }
      })

    if (faqEntries.length > 0) {
      const { id } = getCMSWrapperProps(faqContent)

      return {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#${id}`,
        mainEntity: faqEntries.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer,
          },
        })),
      }
    }
  }
}
